import * as React from 'react';
import { has as _has } from 'lodash';
import { connect } from 'react-redux';
import { GlobalPrivateState, getConfigurationItems, compareResetDataAndSetDefaults, RequireResetKeys } from 'src/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faInfo } from '@fortawesome/free-solid-svg-icons';
import { Tooltip } from 'reactstrap';
import { FormattedMessage } from 'react-intl';
import MESSAGES from './Warnings.messages';
import { ConfigurationMainModel, ConfigurationDefaultModel, TroughModel, GetDistanceModel, PlateBuilder, Configuration } from 'franke-trough-dependencies';

import { getResetDispatchData } from 'src/api/warnings/get-reset-dispatch-data';
import { requestAPIActions } from 'loopback-react-request';
import './WarningsTooltip.scss';

export interface WarningsTooltipProps {
  plateBuilder: GlobalPrivateState['plateBuilder'];
  configuration: GlobalPrivateState['configuration'];
  defaults: GlobalPrivateState['configurationDefaults'];
  dispatch: Function;
  itemKey: RequireResetKeys;
  warning: GlobalPrivateState['warning'];
}

export interface WarningsTooltipState {
  itemsToReset?: string[];
  tooltipOpen: boolean;
}

const WarningMessages: React.SFC<{itemKey: RequireResetKeys}> = (props) => {
  switch (props.itemKey) {
    case 'trough': 
      return <FormattedMessage {...MESSAGES.dynamic.trough} />;
    case 'qty': 
      return <FormattedMessage {...MESSAGES.dynamic.qty} />;
    case 'typeOfInstallation': 
      return <FormattedMessage {...MESSAGES.dynamic.typeOfInstallation} />;
    case 'plateWidth': 
      return <FormattedMessage {...MESSAGES.dynamic.plateWidth} />;
    case 'plateDepth': 
      return <FormattedMessage {...MESSAGES.dynamic.plateDepth} />;
    case 'distances': 
      return <FormattedMessage {...MESSAGES.dynamic.distances} />;
    case 'cornerDesign': 
      return <FormattedMessage {...MESSAGES.dynamic.cornerDesign} />;
    default:
      return <FormattedMessage {...MESSAGES.defaults.requireResetDefault} />;
  }
};

export class WarningsTooltipPartial extends React.Component<WarningsTooltipProps, WarningsTooltipState> {
  constructor(props: WarningsTooltipProps) {
    super(props);
    this.state = {
      tooltipOpen: false,
    };
    this.handleClick = this.handleClick.bind(this);
    this.toggle = this.toggle.bind(this);
  }

  componentDidMount() {
    if (!this.state.itemsToReset && this.itemsToReset) {
      this.setState({
        itemsToReset: this.itemsToReset
      });
    }
  }
  
  componentDidUpdate() {
    if (this.itemsToReset && !this.state.itemsToReset) {
      this.setState({
        itemsToReset: this.itemsToReset
      });
    }
  }

  public render() {
    const {
      itemKey,
      warning
    } = this.props;
    const id = `${itemKey}_require_reset`;
    const minPlateWidth = this.defaults && this.defaults.washBasinDimension.plateWidth.values.min;
    return warning.requireReset[itemKey] && warning.requireReset[itemKey].required ? (
      <div className="warnings-wrapper">
        <span className="info-icon" id={id}>
          <FontAwesomeIcon icon={faInfo} />
          <Tooltip 
            autohide={false}
            isOpen={this.state.tooltipOpen} 
            placement="bottom"
            toggle={this.toggle}
            target={id}
          >
            <div>
              <p className="text-left mb-2">
                <strong>
                  <FormattedMessage {...MESSAGES.defaults.requireReset} />
                </strong>
              </p>
              {this.itemsToReset && (
                <ul className="text-left mb-2">
                  {this.itemsToReset.map((name, index) => {
                    const key = `${id}_item_${index}`;
                    return (
                      <li key={key}>
                        {MESSAGES.defaults[name] ? 
                          (<FormattedMessage {...MESSAGES.defaults[name]} />) 
                          : 
                          (<span>{name}</span>)
                        }
                    {name === 'plateWidth' && minPlateWidth && ( <span>&nbsp;( min {minPlateWidth} )&nbsp;</span> )}
                      </li>
                    );
                  })}
                </ul>
              )}
              <p className="text-right">
                <strong>
                  <FormattedMessage {...MESSAGES.defaults.requireResetContinue} />
                </strong>
                <button
                  className="btn btn-secondary yes"
                  onClick={() => this.handleClick('approve')}
                  name="approve"
                >
                  <FontAwesomeIcon icon={faCheck} />
                </button>
              </p>
            </div>
          </Tooltip>
        </span>
      </div>
    ) : null;
  }

  private get configuration(): ConfigurationMainModel | undefined {
    return this.props.configuration.result && getConfigurationItems(this.props.configuration.result);
  }

  private get defaults(): ConfigurationDefaultModel | undefined {
    const configuration = this.props.configuration.result;
    const defaults = (configuration && configuration.defaults) || this.props.defaults.result;

    if (this.itemsToReset && this.itemsToReset.indexOf('distances') > -1 ) {
      if (this.props.plateBuilder && defaults) {
        defaults.washBasinDimension.distances.default = this.props.plateBuilder.calc.troughInitialPositions();
      }
    }
    return defaults;
  }

  private handleClick(type: 'approve' | 'decline') {
    if (type === 'approve') {
      this.dispatchConfiguration();
    }
  }

  private get itemsToReset(): string[] | undefined {
    const {
      itemKey,
      warning
    } = this.props;
    const warnings = warning.requireReset[itemKey];
    if (!warnings) {
      return;
    }
    const items: string[] = [];
    warnings.affected.forEach(key => {
      if (warnings.values && warnings.values[key]) {
        items.push(key);
      }
    });
    return items.length > 0 ? items : undefined;
  }

  private toggle() {
    this.setState({
      tooltipOpen: !this.state.tooltipOpen
    });
  }

  private dispatchConfiguration() {
    if (this.itemsToReset && this.itemsToReset.length > 0 && this.configuration) {
      const data = compareResetDataAndSetDefaults(this.itemsToReset, getResetDispatchData(this.configuration, this.itemsToReset), this.defaults);
      const configurationId = this.props.configuration.result && this.props.configuration.result.id;
      if (configurationId) {
        this.props.dispatch(requestAPIActions.load(
          'configuration', {
            url: `/Configurations/${configurationId}`,
            options: {
              method: 'PATCH',
              data
            },
          },
          true,
        ));
      }
    }
  }
}

export const WarningsTooltip = connect((state: GlobalPrivateState) => ({
  plateBuilder: state.plateBuilder,
  configuration: state.configuration,
  defaults: state.configurationDefaults,
  warning: state.warning,
}))(WarningsTooltipPartial);

export default WarningsTooltip;


